// v10/src/components/admin/inventory/modals/MovementsHistoryModal.js
//
// Vista completa del histórico de movimientos para un item del Stock A8.
// Reemplaza al placeholder "Ver todos los movimientos" del DetailModal.
//
// Endpoint:
//   GET /api/inventory-movements?item_id=...&type=...&aircraft_id=...
//                              &date_from=...&date_to=...&limit=50&offset=0
//
// Filtros:
//   - Tipo (intake | dispatch | adjustment | todos)
//   - Aeronave (sólo aplicable a movimientos dispatch — del listado dado)
//   - Rango de fechas (date_from / date_to)
//   - Botón "Limpiar filtros"
// Paginación cliente: 50 por página, botones Anterior / Siguiente.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, AlertCircle, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const PAGE_SIZE = 50;

const formatStock = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 3, minimumFractionDigits: 0 });
};

const formatDateTime = (value, locale = 'es-AR') => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const MovementsHistoryModal = ({
  isOpen,
  itemId,
  itemName = '',
  stockUnitLabel = '',
  aircraft = [],
  onClose,
  darkMode,
  locale = 'es-AR',
}) => {
  const { t } = useTranslation('admin');

  const [filters, setFilters] = useState({
    type: '',
    aircraft_id: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage]             = useState(0);
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const load = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    const params = {
      item_id: itemId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (filters.type)        params.type        = filters.type;
    if (filters.aircraft_id) params.aircraft_id = filters.aircraft_id;
    if (filters.date_from)   params.date_from   = filters.date_from;
    if (filters.date_to)     params.date_to     = filters.date_to;

    inventoryService.listMovements(params)
      .then((res) => {
        const data = res?.data || {};
        setRows(Array.isArray(data.data) ? data.data : []);
        const totalNum = Number(data.total ?? data.count ?? 0);
        setTotal(Number.isFinite(totalNum) ? totalNum : 0);
      })
      .catch(() => {
        setError(t('inventory_modal_movements_history_error'));
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [itemId, page, filters, t]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, load]);

  // Cuando cambian los filtros, volvemos a la página 0.
  useEffect(() => {
    if (!isOpen) return;
    setPage(0);
  }, [filters.type, filters.aircraft_id, filters.date_from, filters.date_to, isOpen]);

  // ESC para cerrar
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const hasFilters = !!(filters.type || filters.aircraft_id || filters.date_from || filters.date_to);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromN = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const toN   = Math.min(total, page * PAGE_SIZE + rows.length);

  const handleClear = () => setFilters({ type: '', aircraft_id: '', date_from: '', date_to: '' });

  if (!isOpen || !itemId) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const inputStyle = {
    padding: '8px 10px',
    borderRadius: 10,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
    color: fgPrimary,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: 0,
  };

  return (
    <AnimatePresence>
      <motion.div
        key="movements-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 110,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }}
          style={{
            width: '100%', maxWidth: 920, maxHeight: '88vh',
            display: 'flex', flexDirection: 'column',
            borderRadius: 24,
            background: darkMode ? '#0f172a' : 'white',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '18px 22px',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t('inventory_modal_movements_history_title', { name: itemName || '' })}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
                {t('inventory_modal_movements_history_pagination', { from: fromN, to: toN, total })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => load()}
              aria-label={t('inventory_modal_movements_history_refresh', { defaultValue: 'Actualizar' })}
              title={t('inventory_modal_movements_history_refresh', { defaultValue: 'Actualizar' })}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 6, color: fgMuted, display: 'flex',
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('inventory_modal_movements_history_close', { defaultValue: 'Cerrar' })}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 6, color: fgMuted, display: 'flex',
              }}
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>

          {/* Filtros */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 10,
            padding: '14px 22px',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
          }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: fgMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('inventory_modal_movements_history_filter_type')}
              </span>
              <select
                value={filters.type}
                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                style={inputStyle}
              >
                <option value="">{t('inventory_modal_movements_history_filter_type_all', { defaultValue: 'Todos' })}</option>
                <option value="intake">{t('inventory_modal_movement_type_intake')}</option>
                <option value="dispatch">{t('inventory_modal_movement_type_dispatch')}</option>
                <option value="adjustment">{t('inventory_modal_movement_type_adjustment')}</option>
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: fgMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('inventory_modal_movements_history_filter_aircraft')}
              </span>
              <select
                value={filters.aircraft_id}
                onChange={(e) => setFilters((f) => ({ ...f, aircraft_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">{t('inventory_modal_movements_history_filter_aircraft_all', { defaultValue: 'Todas' })}</option>
                {aircraft.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.registration ? `${a.registration}${a.model_name ? ` · ${a.model_name}` : ''}` : a.model_name || a.id}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: fgMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('inventory_modal_movements_history_filter_date_from')}
              </span>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: fgMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('inventory_modal_movements_history_filter_date_to')}
              </span>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                style={inputStyle}
              />
            </label>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClear}
                disabled={!hasFilters}
                style={{
                  padding: '9px 12px', borderRadius: 10, border: 'none', cursor: hasFilters ? 'pointer' : 'not-allowed',
                  background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  color: hasFilters ? fgPrimary : fgFaint,
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                  width: '100%',
                }}
              >
                {t('inventory_modal_movements_history_filter_clear')}
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 22px' }}>
            {loading && rows.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '24px 0', color: fgMuted, fontSize: 13 }}>
                <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
                <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                {t('inventory_modal_movements_history_loading')}
              </div>
            ) : error ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                color: '#dc2626', fontSize: 13, fontWeight: 600,
              }}>
                <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
                {error}
              </div>
            ) : rows.length === 0 ? (
              <p style={{ margin: '24px 0', textAlign: 'center', fontSize: 13, color: fgMuted }}>
                {t('inventory_modal_movements_history_empty')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((m) => {
                  const sign = m.movement_type === 'intake' ? '+' : (m.movement_type === 'dispatch' ? '-' : '');
                  const color = m.movement_type === 'intake' ? '#16a34a'
                              : m.movement_type === 'dispatch' ? '#ea580c'
                              : '#6366f1';
                  const bg = `${color}20`;
                  return (
                    <div key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', borderRadius: 12,
                      background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                    }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 11, fontWeight: 800,
                        padding: '4px 9px', borderRadius: 999,
                        background: bg, color, textTransform: 'uppercase', letterSpacing: '0.4px',
                      }}>
                        {t(`inventory_modal_movement_type_${m.movement_type}`)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: fgPrimary }}>
                          {sign}{formatStock(m.quantity, locale)}{stockUnitLabel ? ` ${stockUnitLabel}` : ''}
                          {m.aircraft_registration ? ` → ${m.aircraft_registration}` : ''}
                          {m.supplier ? ` · ${m.supplier}` : ''}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11.5, color: fgMuted }}>
                          {formatDateTime(m.created_at, locale)}
                          {m.recorded_by_name ? ` · ${m.recorded_by_name}` : ''}
                          {m.reason ? ` · ${m.reason}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer / paginación */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            padding: '12px 22px',
            borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
          }}>
            <span style={{ fontSize: 12, color: fgMuted }}>
              {t('inventory_modal_movements_history_pagination', { from: fromN, to: toN, total })}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '8px 12px', borderRadius: 10, border: 'none',
                  cursor: page === 0 || loading ? 'not-allowed' : 'pointer',
                  background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  color: page === 0 || loading ? fgFaint : fgPrimary,
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                }}
              >
                <ChevronLeft style={{ width: 14, height: 14 }} />
                {t('inventory_modal_movements_history_prev', { defaultValue: 'Anterior' })}
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '8px 12px', borderRadius: 10, border: 'none',
                  cursor: page >= totalPages - 1 || loading ? 'not-allowed' : 'pointer',
                  background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  color: page >= totalPages - 1 || loading ? fgFaint : fgPrimary,
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                }}
              >
                {t('inventory_modal_movements_history_next', { defaultValue: 'Siguiente' })}
                <ChevronRight style={{ width: 14, height: 14 }} />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MovementsHistoryModal;
